import { SCHEDULE_TIMEZONE } from "@Simplicity-Education/core";
import type { Destination } from "../../configTypes";
import type {
  AgentBuild,
  OnsiteAgent,
  OnsiteRun,
  OnsiteWorkspace,
  OutputValue,
  PlanAction,
  PlanChange,
  PlanSummary,
  PlanValue,
  WorkspaceSettingsPatch,
} from "./types";

// Mirror WORKSPACE_NAME, REGION and ZONE in apps/platform/src/features/onsite/onsiteWorkspace.ts.
const WORKSPACE_NAME = /^[a-z][a-z0-9-]{0,7}$/;
const REGION = /^[a-z]+-[a-z]+[0-9]$/;
const ZONE = /^[a-z]+-[a-z]+[0-9]-[a-z]$/;
const PLAN_ACTIONS: readonly PlanAction[] = [
  "import",
  "create",
  "update",
  "delete",
  "replace",
  "read",
];
const ACTIVE_STATUSES = ["pending", "running"];
// The agent runs as this Cloud Run job in the client's project (step0Plan.ts).
const AGENT_LOG_QUERY =
  'resource.type="cloud_run_job" AND resource.labels.job_name="simplicity-agent"';
const LOG_MARGIN_MS = 2 * 60_000;
const RUN_POLL_MS = 10_000;

const SFTP_IP_OUTPUT = "sftp_server_vm_external_ip";
const PLACEMENT_KEYS = ["region", "zone"] as const;
// Statuses where the client's project already holds what the agent set up.
const PROVISIONED_STATUSES = ["bootstrapped", "applied"];

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

const isScalar = (v: unknown) => ["string", "number", "boolean"].includes(typeof v);

function isValueSide(v: unknown): boolean {
  return v === null || isScalar(v) || (Array.isArray(v) && v.every(isScalar));
}

function isPlanValue(v: unknown): v is PlanValue {
  return (
    isRecord(v) &&
    typeof v.field === "string" &&
    isValueSide(v.before) &&
    isValueSide(v.after) &&
    (v.afterUnknown === undefined || typeof v.afterUnknown === "boolean")
  );
}

/** The optional detail newer agents report; anything malformed is dropped, not shown. */
function detailFrom(value: Record<string, unknown>): Partial<PlanChange> {
  const fields = Array.isArray(value.changedFields)
    ? value.changedFields.filter((f): f is string => typeof f === "string")
    : [];
  const values = Array.isArray(value.values) ? value.values.filter(isPlanValue) : [];
  return {
    ...(value.importing === true && { importing: true as const }),
    ...(typeof value.reason === "string" && { reason: value.reason }),
    ...(fields.length > 0 && { changedFields: fields }),
    ...(values.length > 0 && { values }),
  };
}

function changeFrom(value: unknown): PlanChange[] {
  if (!isRecord(value) || typeof value.address !== "string") return [];
  const action = PLAN_ACTIONS.find((a) => a === value.action);
  return action ? [{ address: value.address, action, ...detailFrom(value) }] : [];
}

function count(value: unknown): number {
  return typeof value === "number" ? value : 0;
}

/** Null when the name is valid, else why not. */
export function workspaceNameError(name: string): string | null {
  if (WORKSPACE_NAME.test(name)) return null;
  return "Use 1–8 characters: lowercase letters, digits or '-', starting with a letter.";
}

/** A workspace's placement as a form holds it: text as typed, blank = the module default. */
export interface PlacementForm {
  region: string;
  zone: string;
}

export type PlacementKey = keyof PlacementForm;

const orNull = (text: string) => text.trim() || null;

/** True when both are set and the zone is not inside the region, as the platform checks it. */
function zoneOutsideRegion(region: string | null, zone: string | null): boolean {
  return Boolean(region && zone && !zone.startsWith(`${region}-`));
}

function zoneError(region: string | null, zone: string | null): string | null {
  if (zone === null) return null;
  if (!ZONE.test(zone)) return "Not a GCP zone, e.g. us-east1-b.";
  return zoneOutsideRegion(region, zone) ? `Must be a zone in ${region}, e.g. ${region}-b.` : null;
}

/** Why the platform would refuse each typed placement field; a blank field is never an error. */
export function placementErrors(form: PlacementForm): Partial<Record<PlacementKey, string>> {
  const region = orNull(form.region);
  const regionOk = region === null || REGION.test(region);
  const zone = zoneError(regionOk ? region : null, orNull(form.zone));
  return {
    ...(!regionOk && { region: "Not a GCP region, e.g. us-east1." }),
    ...(zone !== null && { zone }),
  };
}

/** The placement a create request carries; a blank field is null, so the module default applies. */
export function placementValues(form: PlacementForm): Record<PlacementKey, string | null> {
  return {
    region: orNull(form.region),
    zone: orNull(form.zone),
  };
}

export function parseSourceRanges(text: string): string[] {
  return text
    .split(",")
    .map((range) => range.trim())
    .filter(Boolean);
}

/** The plan a run reported, or null when the run has no (well-formed) result yet. */
export function planOf(run: OnsiteRun): PlanSummary | null {
  if (!isRecord(run.result) || !isRecord(run.result.plan)) return null;
  const plan = run.result.plan;
  if (!Array.isArray(plan.changes)) return null;
  return {
    import: count(plan.import),
    create: count(plan.create),
    update: count(plan.update),
    delete: count(plan.delete),
    replace: count(plan.replace),
    changes: plan.changes.flatMap(changeFrom),
  };
}

/** Scalar Terraform outputs; anything else in the value is dropped. */
function outputsFrom(value: unknown): Record<string, OutputValue> {
  if (!isRecord(value)) return {};
  const outputs: Record<string, OutputValue> = {};
  for (const [key, v] of Object.entries(value)) {
    if (v === null || ["string", "number", "boolean"].includes(typeof v)) {
      outputs[key] = v as OutputValue;
    }
  }
  return outputs;
}

export function runOutputs(run: OnsiteRun): Record<string, OutputValue> {
  return isRecord(run.result) ? outputsFrom(run.result.outputs) : {};
}

/** The agent's last self-check's missing permissions, or null when it has never checked in. */
export function missingPermissions(workspace: OnsiteWorkspace): string[] | null {
  const check = workspace.lastSelfCheck;
  if (!isRecord(check) || !Array.isArray(check.missingPermissions)) return null;
  return check.missingPermissions.filter((p): p is string => typeof p === "string");
}

export function sftpIp(workspace: OnsiteWorkspace): string | null {
  const ip = outputsFrom(workspace.outputs)[SFTP_IP_OUTPUT];
  return typeof ip === "string" && ip ? ip : null;
}

export function isActiveRun(run: OnsiteRun): boolean {
  return ACTIVE_STATUSES.includes(run.status);
}

export function runsRefetchInterval(runs: OnsiteRun[] | undefined): number | false {
  return runs?.some(isActiveRun) ? RUN_POLL_MS : false;
}

/**
 * The plan an apply may be approved from: the newest succeeded plan, unless an apply already
 * consumed it. Matches the server's `assertLatestUnapprovedPlan`.
 */
export function approvablePlan(runs: OnsiteRun[]): OnsiteRun | null {
  let newest: OnsiteRun | null = null;
  for (const run of runs) {
    if (run.action !== "plan" || run.status !== "succeeded") continue;
    if (!newest || run.id > newest.id) newest = run;
  }
  if (!newest || !planOf(newest)) return null;
  const planId = newest.id;
  const applied = runs.some((run) => run.action === "apply" && run.planRunId === planId);
  return applied ? null : newest;
}

/** "13 imports, 3 creates, 1 replace" — non-zero counts, imports first, deletes last. */
export function describeCounts(plan: PlanSummary): string {
  const parts = (["import", "create", "update", "replace", "delete"] as const)
    .filter((action) => plan[action] > 0)
    .map((action) => `${plan[action]} ${action}${plan[action] === 1 ? "" : "s"}`);
  return parts.length > 0 ? parts.join(", ") : "no changes";
}

/**
 * The client's GCP project, read from the runner Project's BigQuery destination. Null unless the
 * active BigQuery destinations name exactly one project, so the form asks instead of guessing.
 */
export function warehouseProjectId(destinations: Destination[]): string | null {
  const ids = new Set(
    destinations
      .filter((d) => d.archivedAt === null && d.type.startsWith("bigquery"))
      .map((d) => d.vars.projectId)
      .filter((id): id is string => typeof id === "string" && id !== ""),
  );
  return ids.size === 1 ? [...ids][0] : null;
}

export function approveConfirmText(plan: PlanSummary, targetLabel: string): string {
  return `Apply ${describeCounts(plan)} to ${targetLabel}?`;
}

export function formatDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function isDestructive(action: PlanAction): boolean {
  return action === "replace" || action === "delete";
}

/** True when the change sets a VM's `desired_status` to RUNNING, which boots a stopped VM. */
export function startsVm(value: PlanValue): boolean {
  return value.field.split(".").pop() === "desired_status" && value.after === "RUNNING";
}

export function formatPlanValue(value: PlanValue["before"]): string {
  if (value === null) return "—";
  return Array.isArray(value) ? `[${value.join(", ")}]` : String(value);
}

/** The GCP project a plan or apply ran in, from its frozen Terraform inputs. */
function runProjectId(run: OnsiteRun): string | null {
  const project = isRecord(run.inputArgs) ? run.inputArgs.project_id : null;
  return typeof project === "string" && project !== "" ? project : null;
}

const shiftIso = (iso: string, ms: number) => new Date(Date.parse(iso) + ms).toISOString();

/**
 * Cloud Logging for the agent job, in the client's project, from 2 minutes before the run was
 * claimed to 2 minutes after it finished. Null until the run has finished.
 */
export function planLogUrl(run: OnsiteRun): string | null {
  const project = runProjectId(run);
  const start = run.claimedAt ?? run.startedAt;
  if (!["plan", "apply"].includes(run.action) || !project || !start || !run.completedAt) {
    return null;
  }
  const range =
    `startTime=${shiftIso(start, -LOG_MARGIN_MS)};` +
    `endTime=${shiftIso(run.completedAt, LOG_MARGIN_MS)}`;
  return (
    `https://console.cloud.google.com/logs/query;query=${encodeURIComponent(AGENT_LOG_QUERY)};` +
    `${range}?project=${encodeURIComponent(project)}`
  );
}

/** A workspace's Terraform settings as the edit form holds them: text as typed, blank = default. */
export interface SettingsForm {
  enableSftpServer: boolean;
  sshSourceRanges: string;
  region: string;
  zone: string;
}

export function settingsFormFrom(workspace: OnsiteWorkspace): SettingsForm {
  return {
    enableSftpServer: workspace.enableSftpServer,
    sshSourceRanges: workspace.sshSourceRanges.join(", "),
    region: workspace.region ?? "",
    zone: workspace.zone ?? "",
  };
}

function sameList(a: string[], b: string[]): boolean {
  return a.length === b.length && a.every((value, i) => value === b[i]);
}

/** Only the settings that differ from the workspace's, so a save never re-sends the rest. */
export function settingsPatch(
  workspace: OnsiteWorkspace,
  form: SettingsForm,
): WorkspaceSettingsPatch {
  const patch: WorkspaceSettingsPatch = {};
  if (form.enableSftpServer !== workspace.enableSftpServer) {
    patch.enableSftpServer = form.enableSftpServer;
  }
  const ranges = parseSourceRanges(form.sshSourceRanges);
  if (!sameList(ranges, workspace.sshSourceRanges)) patch.sshSourceRanges = ranges;
  for (const key of PLACEMENT_KEYS) {
    const value = form[key].trim() || null;
    if (value !== workspace[key]) patch[key] = value;
  }
  return patch;
}

export type SftpToggleNotice = "on" | "off-applied" | "off-unapplied";

/**
 * Which consequence of the SFTP toggle to spell out. Once an agent apply has run, its state holds
 * the server, so turning it off plans a delete; before that the server is simply left behind.
 */
export function sftpToggleNotice(
  workspace: OnsiteWorkspace,
  enableSftpServer: boolean,
): SftpToggleNotice | null {
  if (enableSftpServer === workspace.enableSftpServer) return null;
  if (enableSftpServer) return "on";
  if (workspace.appliedAt !== null) return "off-applied";
  return PROVISIONED_STATUSES.includes(workspace.status) ? "off-unapplied" : null;
}

/** True when the patch moves an applied workspace's region or zone. */
export function movesPlacement(workspace: OnsiteWorkspace, patch: WorkspaceSettingsPatch): boolean {
  return workspace.appliedAt !== null && PLACEMENT_KEYS.some((key) => key in patch);
}

// The two AGENT_VERSION forms that name an image (`<image>@sha256:<hex>`, `<image>:<tag>`).
const AT_DIGEST = /@(sha256:[0-9a-f]{64})$/;
const AT_TAG = /^[^@:]+:([A-Za-z0-9_][A-Za-z0-9_.-]*)$/;
// en-CA formats as YYYY-MM-DD; the month and day are what a build list needs.
const ET_DATE = new Intl.DateTimeFormat("en-CA", {
  timeZone: SCHEDULE_TIMEZONE,
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** The first 12 hex characters of a `sha256:` digest. */
export function shortDigest(digest: string): string {
  return digest.replace(/^sha256:/, "").slice(0, 12);
}

/** "b61bd74ab · 10-23" (upload date, ET) from the release list; else the short digest. */
export function agentBuildLabel(build: AgentBuild): string | null {
  if (build.tag && build.builtAt) {
    return `${build.tag} · ${ET_DATE.format(new Date(build.builtAt)).slice(5)}`;
  }
  return build.digest ? shortDigest(build.digest) : build.tag;
}

export function agentBadgeText(agent: OnsiteAgent): string {
  if (agent.status === "current") return "Current";
  if (agent.status === "unknown") return "Unknown";
  return agent.versionsBehind === null
    ? "Behind"
    : `Behind · ${plural(agent.versionsBehind, "agent version")}`;
}

/** What to do about a behind agent, naming the build setup would move it to. */
export function agentBehindHint(agent: OnsiteAgent): string | null {
  if (agent.status !== "behind") return null;
  const target = agent.current ? agentBuildLabel(agent.current) : null;
  return `Run setup again to move this client to the current agent${target ? ` (${target})` : ""}.`;
}

/** A build named by tag, or by short digest when the release list had none. */
const buildRef = (ref: string) => (ref.startsWith("sha256:") ? shortDigest(ref) : ref);

function agentStateText(agent: OnsiteAgent): string {
  if (agent.status === "current" && agent.sameAgentAs) {
    return `current — same agent as ${buildRef(agent.sameAgentAs)}`;
  }
  if (agent.status !== "behind" || agent.versionsBehind === null) return agent.status;
  return `behind by ${plural(agent.versionsBehind, "agent version")}`;
}

/** "Agent image b61bd74ab · 10-23 (behind by 2 agent versions)". */
export function agentImageLine(agent: OnsiteAgent): string {
  const label = agentBuildLabel(agent);
  return label ? `Agent image ${label} (${agentStateText(agent)})` : "Agent image unknown";
}

/** The agent that claimed a run: its build tag when the workspace's data names it, else its digest. */
export function runAgentLabel(agentVersion: string | null, agent: OnsiteAgent): string | null {
  if (!agentVersion) return null;
  const digest = AT_DIGEST.exec(agentVersion)?.[1];
  if (!digest) return AT_TAG.exec(agentVersion)?.[1] ?? agentVersion;
  const known = [agent, agent.current].find((build) => build?.digest === digest);
  return known?.tag ?? shortDigest(digest);
}
